import { Card, Table } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import APIList from '../../../../http/ApiList';
import { useSelectorSize } from '../../../../hooks/useWindowSize';
import { FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID } from '../../../../enum/common.enum';
import { kpiValue, toCamelCase } from '../../../../utils/farecast-util';
import { Color } from 'antd/es/color-picker';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import { groupBy, isNil, round, sortBy } from 'lodash';
import { getS3Object } from '@meteor/frontend-core';
import { NAME_PATH, useTralitSetting } from '../../../../hooks/useTralitSetting';
import { useLabel } from '../../../../hooks/useLabel';

const RiskTable: React.FC<any> = (props) => {
  const { color, event$, tralitSettings } = useContext(AppContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const label = useLabel();
  // 完整数据
  const [dataProvider, setDataProvider] = useState({ fields: [], compareData: [] });
  // Loading
  const [tableLoading, setTableLoading] = useState(true);
  // 比较对象
  const [compare, setCompare] = useState<any>('snapshot');
  // 比较KPI
  const [compareKpiKey, setCompareKpiKey] = useState();
  // 比较KPI Fields
  const fieldKpi = useMemo(() => {
    const { fields } = dataProvider;
    if (compareKpiKey) {
      return fields.find((field) => compareKpiKey === field.fieldKey) || {};
    }
    const findCompareKPI = fields.find((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.isCompare);
    return findCompareKPI || {};
  }, [dataProvider, compareKpiKey]);
  // Target Label
  const [tarLabel, setTarLabel] = useState();
  // Current Label
  const [curLabel, setCurLabel] = useState();
  // 获取Table滚动条高度
  const { selectorHeight4Table } = useSelectorSize({
    selector: '.tralit-table-tbody',
    watcherSelector: ['.tralit-table-header'],
  });
  const [filteredInfo, setFilteredInfo] = useState<any>();

  /**
   * 调用API获取数据
   */
  const getTableData = (req) => {
    setTableLoading(true);
    setCompareKpiKey(req.metric);
    const {
      fiscalYear,
      evaluationPeriod,
      dimension,
      currentSnapshot,
      targetSnapshot,
      currentPeriod = [],
      targetPeriod = [],
      aggregationPeriod,
      compare,
    } = req;
    const [startDate, endDate] = aggregationPeriod || [];
    const tarLabelMap = {
      snapshot: `断面合計(${targetSnapshot})`,
      period: `期間合計${targetPeriod.length > 0 ? `(${targetPeriod[0].format('YY-MM')} ~ ${targetPeriod[1].format('YY-MM')})` : ''}`,
      plan: label.plan,
      prev_year: '前年',
      prev_month: '前月',
    };
    const curLabelMap = {
      snapshot: `断面合計(${currentSnapshot})`,
      period: `期間合計${currentPeriod.length > 0 ? `(${currentPeriod[0].format('YY-MM')} ~ ${currentPeriod[1].format('YY-MM')})` : ''}`,
      plan: fieldKpi.label,
      prev_year: '今年',
      prev_month: '今月',
    };
    setTarLabel(tarLabelMap[compare]);
    setCurLabel(curLabelMap[compare]);
    const paramMap = {
      snapshot: {
        snapshot: [currentSnapshot, targetSnapshot],
        compare: 'snapshot',
        reportType: 'monthly',
      },
      period: {
        snapshot: [currentSnapshot],
        compare: 'period',
        reportType: 'monthly',
        timeFilter: [
          {
            startDate: currentPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: currentPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
          {
            startDate: targetPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: targetPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
        ],
      },
      plan: {
        snapshot: [currentSnapshot],
        compare: 'plan',
        reportType: 'monthly',
      },
      prev_year: {
        snapshot: [currentSnapshot],
        compare: 'prev_year',
        reportType: 'monthly',
      },
      prev_month: {
        snapshot: [currentSnapshot],
        compare: 'prev_month',
        reportType: 'monthly',
      },
    };
    const params = {
      websocketRoute: 'WebsocketOpenaiEvent',
      client_account: tralitSettings.account,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      fiscalYear,
      evaluationPeriod,
      reportType: dimension,
      ...paramMap[compare],
    };
    APIList.getRiskTableData()
      .post(params)
      .then(async (res) => {
        const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME, res.bucketKey);
        setDataProvider({
          ...response,
          fields: response.fields.filter((field) => !field.classification || field.classification.includes('risk')),
        });
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  /**
   * 事件监听：PAGE_INIT， FILTER_ITEM_CHANGED
   */
  event$.useSubscription((globalEvent) => {
    if (!globalEvent || globalEvent.pageId !== PAGE_ID.RISK) {
      return;
    }
    if (globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT) {
      setCompare(globalEvent.data.compare);
      getTableData(globalEvent.data);
    }
    if (globalEvent.eventType === GLOBAL_EVENT.FILTER_ITEM_CHANGED) {
      setCompare(globalEvent.data.compare);
      getTableData(globalEvent.data);
    }
    if (globalEvent.eventType === GLOBAL_EVENT.CHART_ITEM_CLICK) {
      setFilteredInfo(globalEvent.data);
    }
  });

  // Table Columns
  const columns = useMemo(() => {
    const render = (text) => kpiValue(text, fieldKpi.unitValue);

    const { fields } = dataProvider;

    const getFilteredValue = (field) => {
      if (filteredInfo?.fieldKey) {
        const fieldKeys = filteredInfo?.fieldKey.split('&');
        if (typeof filteredInfo?.value === 'string') {
          const values = filteredInfo?.value.split('&');
          return fieldKeys.reduce((acc, fieldKey, index) => {
            if (fieldKey === field.fieldKey) {
              const value = values[index];
              acc.push(value);
            }
            return acc;
          }, []);
        } else if (filteredInfo?.fieldKey === field.fieldKey) {
          return filteredInfo?.value;
        }
      }
      return null;
    };

    const dynamicColumns = sortBy(fields, 'priority')
      .filter((field) => field.category === 'drill_down')
      .map((field) => ({
        key: field.fieldKey,
        dataIndex: toCamelCase(field.fieldKey),
        title: field.label,
        width: field.width || 100,
        sorter: (a, b) => a[field.fieldKey] - b[field.fieldKey],
        filterSearch: true,
        filteredValue: getFilteredValue(field),
        filters: Object.keys(groupBy(dataProvider.compareData, toCamelCase(field.fieldKey)))
          .sort()
          .map((item) => ({ text: item, value: item })),
        onFilter: (value, record) => record[toCamelCase(field.fieldKey)]?.startsWith(value),
      }));
    const staticColumns = [
      {
        key: 'current',
        dataIndex: toCamelCase(fieldKpi.fieldKey),
        title: fieldKpi?.label,
        width: 100,
        className: 'text-right',
        render,
        sorter: (a, b) => a.target - b.target,
      },
    ];
    return [...dynamicColumns, ...staticColumns];
  }, [dataProvider, fieldKpi, tarLabel, curLabel, filteredInfo]);

  useEffect(() => {
    setTableLoading(true);
  }, [tralitSettings.account]);

  return (
    <>
      <Card
        styles={{ body: { padding: 0 }, header: { color: '#555', fontWeight: 'bold', fontSize: fontSize.tableTitle, padding: '0 12px' } }}
        classNames={{ header: 'list-card-title' }}
        title={`${fieldKpi.label || ''}詳細一覧`}
        extra={`単位: ${fieldKpi.unitName || '円'}`}
      >
        <Table
          virtual
          size="small"
          className="small-header"
          bordered
          rowHoverable={false}
          rowKey={(record) => dataProvider.fields.map((field) => record[toCamelCase(field.fieldKey)]).join()}
          columns={columns}
          dataSource={dataProvider.compareData}
          pagination={false}
          scroll={{
            x: 'max-content',
            y: selectorHeight4Table,
          }}
          loading={tableLoading}
          onChange={(pagination, filters) => {
            console.log(filters);
            setFilteredInfo(
              Object.keys(filters).reduce((acc: any, key) => {
                if (filters[key]) {
                  acc.fieldKey = key;
                  acc.value = filters[key];
                }
                return acc;
              }, {})
            );
          }}
        />
      </Card>
    </>
  );
};

export default RiskTable;
